import { ADMISSION_CHECK, ADMISSION_READINESS } from './labels';

export function hasOpenBlockingExceptions(admission) {
  const blockers = (admission?.blockers || []).filter((item) => item && item.status !== 'CURED');
  if (blockers.length > 0) return true;
  return (admission?.exceptions || []).some((item) => {
    if (!item || item.status === 'CURED') return false;
    const severity = String(item.severity || '').toUpperCase();
    return severity === 'HIGH' || severity === 'CRITICAL';
  });
}

export function canCertifyAdmission(admission) {
  if (!admission) return false;
  if (admission.readiness_status !== ADMISSION_READINESS.READY_TO_CERTIFY) return false;
  if (Number(admission.blocking_count) > 0) return false;
  if (hasOpenBlockingExceptions(admission)) return false;
  return true;
}

/** Provider return leaves package status unchanged and sets admission readiness. */
export function admissionNeedsOriginator(admission) {
  const status = admission?.readiness_status;
  return Boolean(status) && status !== ADMISSION_READINESS.CERTIFIED;
}

export function isEvaluatingAdmission(admission) {
  return admission?.readiness_status === ADMISSION_READINESS.EVALUATING;
}

export function isTerminalAdmissionReadiness(status) {
  return Boolean(status) && status !== ADMISSION_READINESS.EVALUATING;
}

export function documentAssessmentById(admission, documentId) {
  if (!admission || !documentId) return null;
  const rows = admission.document_assessments || [];
  return rows.find((row) => String(row.document_id) === String(documentId)) || null;
}

export function authoritativeCandidateCount(admission) {
  const rows = admission?.document_assessments || [];
  return rows.filter((row) => row.vault_role === 'AUTHORITATIVE_CANDIDATE').length;
}

export function supportingDocumentCount(admission) {
  const rows = admission?.document_assessments || [];
  return rows.filter((row) => row.vault_role === 'SUPPORTING').length;
}

export function evidenceReferenceCount(admission) {
  const refs = new Set();
  (admission?.document_assessments || []).forEach((row) => {
    (row.evidence_references || []).forEach((code) => {
      if (code) refs.add(code);
    });
  });
  return refs.size;
}

export function openAdmissionExceptions(admission) {
  return (admission?.exceptions || []).filter((item) => item.status !== 'CURED');
}

export function confirmableFactsFromAssessment(documentAdmission) {
  const facts = documentAdmission?.confirmable_facts;
  if (!Array.isArray(facts)) return [];
  return facts.filter((item) => item && (item.confirmable === true || item.requires_confirmation === true));
}

export function requiredFactCodes(admission, documentId) {
  const rows = admission?.exceptions || [];
  return rows
    .filter((item) => {
      if (!item.fact_code || item.status === 'CURED') return false;
      if (!documentId) return true;
      return !item.document_id || String(item.document_id) === String(documentId);
    })
    .map((item) => item);
}

export function fileActionLabel(check) {
  if (check === ADMISSION_CHECK.BLOCKED) return 'Resolve';
  if (check === ADMISSION_CHECK.REVIEW_NEEDED) return 'Review';
  return 'Review admission details';
}

export function admissionNextActionLabel(admission) {
  const status = admission?.readiness_status;
  if (status === ADMISSION_READINESS.BLOCKED) return 'Cure admission exception';
  if (status === ADMISSION_READINESS.RETURNED) return 'Cure returned facts';
  if (status === ADMISSION_READINESS.REVIEW_REQUIRED) return 'Confirm admission details';
  if (status === ADMISSION_READINESS.READY_TO_CERTIFY) return 'Certify package';
  if (status === ADMISSION_READINESS.CERTIFIED) return 'Provider review';
  if (status === ADMISSION_READINESS.EVALUATING) return 'Processing admission checks…';
  return null;
}

export function hasUsableAdmissionProfile(program) {
  return Boolean(program?.admission_profile) && program.admission_blocked_code !== 'ADM-001';
}

export function programmeProfileUnavailableMessage(program) {
  const code = program?.admission_blocked_code || 'ADM-001';
  return `This programme has no approved Admission Profile (${code}). Ask the Provider to activate a Programme Admission Profile before you can continue.`;
}
